import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { usersApi, type AdminUser, type AdminUsersListParams, type UserStatus } from '@/api/users';
import { coursesApi } from '@/api/courses';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { SearchInput } from '@/components/ui/SearchInput';
import { Select } from '@/components/ui/Select';
import { Avatar } from '@/components/ui/Avatar';
import { humanizeError } from '@/lib/errors';
import { StudentFormModal } from './StudentFormModal';
import { BulkStudentsModal } from './BulkStudentsModal';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MPickerChip } from '@/components/mobile/MPickerChip';
import { MAdd, MBlock, MEmpty, MTag, MTitle } from '@/components/mobile/MobileKit';

// Status option labels and status cell labels are built inside the component
// so they have access to t(). Kept as a helper that takes t() at call site.

export function AdminStudentsPage() {
  const ask = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [filters, setFilters] = useState<AdminUsersListParams>({
    role: 'student',
    offset: 0,
    limit: 20,
  });
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const isMobile = useIsMobile();
  const [addSheet, setAddSheet] = useState(false);
  const [sheetFor, setSheetFor] = useState<AdminUser | null>(null);

  const list = useQuery({
    queryKey: ['admin', 'students', filters],
    queryFn: () => usersApi.list(filters),
  });

  // Списки для фильтров-выпадашек (вместо ввода UUID вручную).
  const marathonsList = useQuery({
    queryKey: ['admin', 'students', 'marathon-options'],
    queryFn: () => coursesApi.listAllMarathons({ limit: 100, template: 'false' }),
  });
  const curatorsList = useQuery({
    queryKey: ['admin', 'students', 'curator-options'],
    queryFn: () => usersApi.list({ role: 'curator', limit: 100 }),
  });

  const STATUS_OPTS = [
    { value: '', label: t('adminStudents.statusAll') },
    { value: 'active', label: t('adminStudents.statusActive') },
    { value: 'blocked', label: t('adminStudents.statusBlocked') },
    { value: 'deleted', label: t('adminStudents.statusDeleted') },
  ];

  const statusLabel = (s: UserStatus) => {
    const map: Record<UserStatus, string> = {
      active: t('adminStudents.statusActive'),
      blocked: t('adminStudents.statusBlocked'),
      deleted: t('adminStudents.statusDeleted'),
    };
    return map[s] ?? s;
  };

  const marathonOptions = [
    { value: '', label: t('adminStudents.allMarathons') },
    ...(marathonsList.data?.items?.map((m) => ({ value: m.id, label: m.title })) ?? []),
  ];
  const curatorOptions = [
    { value: '', label: t('adminStudents.allCurators') },
    ...(curatorsList.data?.items?.map((c) => ({ value: c.id, label: c.full_name })) ?? []),
  ];

  const blockUser = useMutation({
    mutationFn: (id: string) => usersApi.block(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'students'] }),
  });

  const deleteUser = useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'students'] }),
  });

  const actionError = blockUser.error
      ? humanizeError(blockUser.error)
      : deleteUser.error
        ? humanizeError(deleteUser.error)
        : null;

  const askBlock = async (u: AdminUser) => {
    if (await ask({ message: t('adminStudents.confirmBlock', { name: u.full_name }), danger: true })) blockUser.mutate(u.id);
  };
  const askDelete = async (u: AdminUser) => {
    if (await ask({ message: t('adminStudents.confirmDelete', { name: u.full_name }), danger: true })) deleteUser.mutate(u.id);
  };
  const modals = (
    <>
      <BulkStudentsModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['admin', 'students'] })}
      />
      <StudentFormModal
        open={creatingOpen}
        onClose={() => setCreatingOpen(false)}
        onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'students'] })}
      />
    </>
  );

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §5.5) ──────────────────────────
  // «+» → лист «Добавить ученика / Добавить группу» вместо двух кнопок шапки; фильтры — фишками с окном выбора (правило
  // 7); строка — имя, почта, статус, «куратор · курсов N · прогресс %»; «⋯» — блокировка и удаление с прежними
  // подтверждениями.
  if (isMobile) {
    const setFilter = (patch: Partial<AdminUsersListParams>) => setFilters((f) => ({ ...f, ...patch, offset: 0 }));
    const items = list.data?.items ?? [];
    // Лист предлагает только то, что применимо: «Заблокировать» — активному (снять блокировку — в карточке),
    // «Удалить» — пока не удалён. Нечего предложить — «⋯» в строке нет.
    const rowActions = (u: AdminUser) => [
      ...(u.status === 'active'
        ? [{ label: t('adminStudents.actionBlock'), icon: 'block', danger: true, onSelect: () => askBlock(u) }]
        : []),
      ...(u.status !== 'deleted'
        ? [{ label: t('common.delete'), icon: 'delete', danger: true, onSelect: () => askDelete(u) }]
        : []),
    ];
    return (
      <div className="space-y-3">
        <MTitle
          title={t('adminStudents.title')}
          subtitle={list.data ? t('adminStudents.totalStudents', { count: list.data.total }) : undefined}
          right={<MAdd label={t('adminStudents.add')} onClick={() => setAddSheet(true)} />}
        />
        <SearchInput
          placeholder={t('adminStudents.searchPlaceholder')}
          value={filters.q ?? ''}
          onSearch={(q) => setFilter({ q: q || undefined })}
        />
        <div className="-mx-5 flex gap-1.5 overflow-x-auto px-5 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <MPickerChip
            label={t('adminStudents.colStatus')}
            value={filters.status ?? ''}
            options={STATUS_OPTS}
            onChange={(v) => setFilter({ status: (v || undefined) as UserStatus | undefined })}
          />
          <MPickerChip
            label={t('adminDetail.course')}
            value={filters.marathon_id ?? ''}
            options={marathonOptions}
            onChange={(v) => setFilter({ marathon_id: v || undefined })}
          />
          <MPickerChip
            label={t('adminStudents.colCurator')}
            value={filters.curator_id ?? ''}
            options={curatorOptions}
            onChange={(v) => setFilter({ curator_id: v || undefined })}
          />
        </div>
        {actionError && <p className="px-0.5 text-[13px] text-error">{actionError}</p>}
        {list.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <MEmpty icon="group" title={t('adminStudents.empty')} />
        ) : (
          <>
            <MBlock>
              {items.map((u, i) => (
                <div
                  key={u.id}
                  data-admin-student-row
                  className={cn('flex items-center gap-2 py-3', i < items.length - 1 && 'border-b border-outline')}
                >
                  <Link to={`/admin/students/${u.id}`} className="flex min-w-0 flex-1 items-center gap-3">
                    <Avatar name={u.full_name} src={u.avatar_url} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate text-[14px] font-semibold text-on-surface">{u.full_name}</span>
                        {u.status !== 'active' && (
                          <MTag tone={u.status === 'blocked' ? 'warm' : 'muted'}>{statusLabel(u.status)}</MTag>
                        )}
                      </span>
                      <span className="block truncate text-[11.5px] text-on-surface-variant">{u.email}</span>
                      <span className="block truncate text-[11.5px] text-on-surface-variant">
                        {[
                          u.curator_name,
                          t('adminStudents.marathonsCount', { count: u.marathons_count ?? 0 }),
                          u.progress != null ? `${Math.max(0, Math.min(100, Math.round(u.progress)))}%` : '',
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                  </Link>
                  {rowActions(u).length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSheetFor(u)}
                      aria-label={t('nav.more')}
                      className="grid h-11 w-11 flex-none place-items-center rounded-full text-on-surface-variant"
                    >
                      <span className="material-symbols-outlined text-[20px]">more_horiz</span>
                    </button>
                  )}
                </div>
              ))}
            </MBlock>
            <Pagination
              offset={filters.offset ?? 0}
              limit={filters.limit ?? 20}
              total={list.data?.total ?? 0}
              onChange={(offset) => setFilters((f) => ({ ...f, offset }))}
            />
          </>
        )}
        <ActionSheet
          open={addSheet}
          onClose={() => setAddSheet(false)}
          actions={[
            { label: t('adminStudents.add'), icon: 'person_add', onSelect: () => setCreatingOpen(true) },
            { label: t('adminStudents.addMany'), icon: 'group_add', onSelect: () => setBulkOpen(true) },
          ]}
        />
        <ActionSheet
          open={!!sheetFor}
          onClose={() => setSheetFor(null)}
          title={sheetFor?.full_name}
          actions={sheetFor ? rowActions(sheetFor) : []}
        />
        {modals}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('adminStudents.title')}
        subtitle={
          // Склонение — формами i18n (было зашито по-русски в коде и на en/kk не переводилось, 28.09.2026).
          list.data ? t('adminStudents.totalStudents', { count: list.data.total }) : t('common.loading')
        }
        actions={
          /*
            Кнопки «Импорт CSV» здесь больше нет — решение владельца 27.08.2026.
            Она не заводила учеников, а дозаполняла анкеты уже существующим и
            требовала столбец user_id: администратор со списком группы получал
            непонятный отказ по-английски. Массовое добавление делается
            отдельным экраном.
          */
          <>
            <Button variant="secondary" onClick={() => setBulkOpen(true)}>
              <span className="material-symbols-outlined">group_add</span>
              {t('adminStudents.addMany')}
            </Button>
            <Button onClick={() => setCreatingOpen(true)}>
              <span className="material-symbols-outlined">person_add</span>
              {t('adminStudents.add')}
            </Button>
          </>
        }
      />

      {actionError && (
        <Card variant="subtle" className="mb-4 text-sm text-error">
          {actionError}
        </Card>
      )}

      {/* Filters */}
      <Card variant="subtle" className="mb-6">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {/* Поиск на сервере — по имени, почте, телефону, курсу и группе записи; запрос — через 300 мс после
              последней буквы, а не на каждую (28.09.2026, вторая волна п. 11). */}
          <SearchInput
            placeholder={t('adminStudents.searchPlaceholder')}
            value={filters.q ?? ''}
            onSearch={(q) => setFilters((f) => ({ ...f, q: q || undefined, offset: 0 }))}
          />
          <Select
            options={STATUS_OPTS}
            value={filters.status ?? ''}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                status: (e.target.value || undefined) as UserStatus | undefined,
                offset: 0,
              }))
            }
          />
          <Select
            options={marathonOptions}
            value={filters.marathon_id ?? ''}
            onChange={(e) =>
              setFilters((f) => ({ ...f, marathon_id: e.target.value || undefined, offset: 0 }))
            }
          />
          <Select
            options={curatorOptions}
            value={filters.curator_id ?? ''}
            onChange={(e) =>
              setFilters((f) => ({ ...f, curator_id: e.target.value || undefined, offset: 0 }))
            }
          />
        </div>
      </Card>

      {/* Results — table on desktop, cards on mobile */}
      {list.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : (list.data?.items?.length ?? 0) === 0 ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('adminStudents.empty')}{' '}
          <button onClick={() => setCreatingOpen(true)} className="font-semibold text-primary">
            {t('adminStudents.addFirst')}
          </button>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <Card className="hidden md:block">
            <table className="w-full">
              <thead className="border-b border-outline-variant/40">
                <tr className="text-left text-xs uppercase tracking-label text-on-surface-variant">
                  <th className="pb-3">{t('adminStudents.colName')}</th>
                  <th className="pb-3">{t('adminStudents.colStatus')}</th>
                  <th className="pb-3">{t('adminStudents.colCurator')}</th>
                  <th className="pb-3">{t('adminStudents.colMarathons')}</th>
                  <th className="pb-3">{t('adminStudents.colProgress')}</th>
                  <th className="pb-3 text-right">{t('adminStudents.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data?.items.map((u) => (
                  <StudentRow
                    key={u.id}
                    user={u}
                    statusLabel={statusLabel}
                    onBlock={() => askBlock(u)}
                    onDelete={() => askDelete(u)}
                  />
                ))}
              </tbody>
            </table>
          </Card>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {list.data?.items.map((u) => (
              <StudentMobileCard key={u.id} user={u} statusLabel={statusLabel} />
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            offset={filters.offset ?? 0}
            limit={filters.limit ?? 20}
            total={list.data?.total ?? 0}
            onChange={(offset) => setFilters((f) => ({ ...f, offset }))}
          />
        </>
      )}

      {modals}
    </>
  );
}

function statusVariant(s: UserStatus) {
  switch (s) {
    case 'active': return 'success';
    case 'blocked': return 'danger';
    default: return 'neutral';
  }
}

function StudentRow({
  user,
  statusLabel,
  onBlock,
  onDelete,
}: {
  user: AdminUser;
  statusLabel: (s: UserStatus) => string;
  onBlock: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <tr className="border-b border-outline-variant/20 last:border-0">
      <td className="py-3">
        <Link to={`/admin/students/${user.id}`} className="flex items-center gap-3">
          <Avatar name={user.full_name} src={user.avatar_url} />
          <div>
            <div className="font-semibold text-on-surface">{user.full_name}</div>
            <div className="text-xs text-on-surface-variant">{user.email}</div>
          </div>
        </Link>
      </td>
      <td><Pill variant={statusVariant(user.status)}>{statusLabel(user.status)}</Pill></td>
      <td className="text-sm text-on-surface-variant">{user.curator_name ?? '—'}</td>
      <td className="text-sm text-on-surface">{user.marathons_count ?? 0}</td>
      <td className="text-sm text-on-surface"><ProgressCell value={user.progress} /></td>
      <td className="text-right">
        <Button size="sm" variant="ghost" onClick={onBlock} title={t('adminStudents.actionBlock')}>
          <span className="material-symbols-outlined">block</span>
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete} title={t('common.delete')}>
          <span className="material-symbols-outlined text-error">delete</span>
        </Button>
        <Link to={`/admin/students/${user.id}`}>
          <Button size="sm" variant="ghost" title={t('adminStudents.actionOpen')}>
            <span className="material-symbols-outlined">arrow_forward</span>
          </Button>
        </Link>
      </td>
    </tr>
  );
}

// ProgressCell renders a compact progress bar. Progress is sourced from
// progress-service; when the backend hasn't populated it we show a dash.
function ProgressCell({ value }: { value?: number }) {
  if (value == null) return <span className="text-on-surface-variant">—</span>;
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-surface-container-highest">
        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-on-surface-variant">{pct}%</span>
    </div>
  );
}

function StudentMobileCard({ user, statusLabel }: { user: AdminUser; statusLabel: (s: UserStatus) => string }) {
  const { t } = useTranslation();
  return (
    <Link to={`/admin/students/${user.id}`}>
      <Card interactive>
        <div className="flex items-center gap-3">
          <Avatar name={user.full_name} src={user.avatar_url} size="lg" />
          <div className="flex-1">
            <div className="font-semibold text-on-surface">{user.full_name}</div>
            <div className="text-xs text-on-surface-variant">{user.email}</div>
            <div className="mt-1.5 flex gap-2">
              <Pill variant={statusVariant(user.status)}>{statusLabel(user.status)}</Pill>
              <Pill variant="neutral">{t('adminStudents.marathonsCount', { count: user.marathons_count ?? 0 })}</Pill>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

